import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProdAuth } from '../../contexts/ProdAuthContext';

const ACCENT = '#4A7C59';

interface Centre {
  id: string;
  name: string;
}

type Step = 'reception' | 'sorting' | 'confirm';

export const BatchForm: React.FC = () => {
  const { user } = useProdAuth();
  const navigate = useNavigate();
  const [centres, setCentres] = useState<Centre[]>([]);
  const [step, setStep] = useState<Step>('reception');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    processType: 'washed',
    collectionCentreId: user?.centreId || '',
    grossCherryKg: '',
    cherryKg: '',     // W_Primary
    mbuniKg: '',      // W_Mbuni
    wasteKg: '',      // W_Waste
    createMbuniBatch: true,
    notes: '',
  });

  useEffect(() => {
    if (['admin', 'super_admin'].includes(user?.role || '')) {
      fetch('/api/production/collection-centres', { credentials: 'include' })
        .then(r => r.json())
        .then(d => setCentres(d.centres || []))
        .catch(() => {});
    }
  }, [user?.role]);

  const gross = parseFloat(form.grossCherryKg) || 0;
  const primary = parseFloat(form.cherryKg) || 0;
  const mbuni = parseFloat(form.mbuniKg) || 0;
  const waste = parseFloat(form.wasteKg) || 0;
  const accounted = primary + mbuni + waste;
  const unaccounted = gross > 0 ? gross - accounted : 0;
  const sortingValid = gross === 0 || Math.abs(unaccounted) < 0.5;

  const inputCls = "w-full px-4 py-3 border border-[#E8E6E2] rounded-lg bg-white text-sm text-[#1A1A1A] focus:outline-none";
  const foc = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`);
  const blu = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => (e.target.style.boxShadow = '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/production/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Failed to create batch');
        return;
      }
      const data = await res.json();
      navigate(`/production/batches/${data.id}`);
    } catch {
      setError('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const STEPS: { id: Step; label: string }[] = [
    { id: 'reception', label: 'Reception' },
    { id: 'sorting', label: 'Sorting' },
    { id: 'confirm', label: 'Confirm' },
  ];

  return (
    <div className="max-w-lg">
      <div className="mb-6">
        <button onClick={() => navigate('/production/batches')} className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A] flex items-center gap-1 mb-3">
          ← Back to batches
        </button>
        <h2 className="text-2xl font-bold text-[#1A1A1A]">New batch</h2>
        <p className="text-sm text-[#6B6B6B] mt-0.5">Cherry reception & sorting</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              onClick={() => i < STEPS.findIndex(x => x.id === step) ? setStep(s.id) : undefined}
              className={`flex items-center gap-2 text-sm font-medium ${step === s.id ? 'text-[#4A7C59]' : i < STEPS.findIndex(x => x.id === step) ? 'text-[#1A1A1A] cursor-pointer' : 'text-[#A3A3A3]'}`}
            >
              <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold ${step === s.id ? 'text-white' : i < STEPS.findIndex(x => x.id === step) ? 'bg-[#4A7C59]/20 text-[#4A7C59]' : 'bg-[#F0EDE8] text-[#A3A3A3]'}`}
                style={step === s.id ? { backgroundColor: ACCENT } : {}}>
                {i + 1}
              </span>
              {s.label}
            </button>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-[#E8E6E2]" />}
          </React.Fragment>
        ))}
      </div>

      {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      {/* ── Step 1: Reception ─────────────────────────────────────────────── */}
      {step === 'reception' && (
        <div className="space-y-5">
          {/* Process type */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-2">
              Primary process <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'washed', label: 'Washed', desc: 'Wet processed' },
                { value: 'natural', label: 'Natural', desc: 'Dry processed' },
                { value: 'honey', label: 'Honey', desc: 'Semi-washed' },
              ].map(({ value, label, desc }) => (
                <button key={value} type="button"
                  onClick={() => setForm(f => ({ ...f, processType: value }))}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${form.processType === value ? 'border-[#4A7C59] bg-[#4A7C59]/5' : 'border-[#E8E6E2] hover:border-[#C0BDB9]'}`}>
                  <p className={`font-semibold text-sm ${form.processType === value ? 'text-[#4A7C59]' : 'text-[#1A1A1A]'}`}>{label}</p>
                  <p className="text-xs text-[#A3A3A3] mt-0.5">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Collection centre (admins) */}
          {['admin', 'super_admin'].includes(user?.role || '') && centres.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Collection centre</label>
              <select value={form.collectionCentreId} onChange={e => setForm(f => ({ ...f, collectionCentreId: e.target.value }))}
                className={inputCls}>
                <option value="">Select centre...</option>
                {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          {/* Gross intake */}
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
              Gross intake (kg) — W<sub>Total</sub>
            </label>
            <input type="number" value={form.grossCherryKg} onChange={e => setForm(f => ({ ...f, grossCherryKg: e.target.value }))}
              placeholder="Total cherries received before sorting" step="0.1" min="0"
              className={inputCls} onFocus={foc} onBlur={blu} />
            <p className="mt-1 text-xs text-[#A3A3A3]">Total weight at reception, before any sorting</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} placeholder="Variety, delivery details, observations..."
              className={`${inputCls} resize-none`} onFocus={foc} onBlur={blu} />
          </div>

          <button onClick={() => setStep('sorting')}
            className="w-full py-3.5 text-white font-medium rounded-lg" style={{ backgroundColor: ACCENT }}>
            Next: Manual sorting →
          </button>
        </div>
      )}

      {/* ── Step 2: Sorting / Mbuni cut ───────────────────────────────────── */}
      {step === 'sorting' && (
        <div className="space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <p className="font-semibold mb-1">Manual sorting — the "Mbuni cut"</p>
            <p className="text-xs">Divert under-ripes (green), over-ripes (dark/raisined), and floaters before pulping. These go to a dedicated drying area as Mbuni (dry process).</p>
          </div>

          {gross > 0 && (
            <div className="bg-white border border-[#E8E6E2] rounded-xl p-4">
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-3">Intake equation: W<sub>Total</sub> = W<sub>Primary</sub> + W<sub>Mbuni</sub> + W<sub>Waste</sub></p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#6B6B6B]">Gross intake</span>
                <span className="font-bold text-[#1A1A1A]">{gross.toLocaleString()} kg</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-[#6B6B6B]">Accounted so far</span>
                <span className={`font-medium ${sortingValid ? 'text-green-600' : 'text-orange-600'}`}>{accounted.toLocaleString()} kg</span>
              </div>
              {!sortingValid && Math.abs(unaccounted) >= 0.5 && (
                <div className="mt-2 text-xs text-orange-600">
                  {unaccounted > 0 ? `${unaccounted.toFixed(1)} kg unaccounted — adjust weights below` : `Over by ${Math.abs(unaccounted).toFixed(1)} kg`}
                </div>
              )}
              {sortingValid && accounted > 0 && (
                <div className="mt-2 text-xs text-green-600">✓ Weights balance</div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
              W<sub>Primary</sub> — Ripe cherries (kg)
            </label>
            <input type="number" value={form.cherryKg} onChange={e => setForm(f => ({ ...f, cherryKg: e.target.value }))}
              placeholder="Ripe cherries for pulping / specialty" step="0.1" min="0"
              className={inputCls} onFocus={foc} onBlur={blu} />
            <p className="mt-1 text-xs text-[#A3A3A3]">Fully ripe cherries destined for {form.processType} processing</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
              W<sub>Mbuni</sub> — Sorted-out cherries (kg)
            </label>
            <input type="number" value={form.mbuniKg} onChange={e => setForm(f => ({ ...f, mbuniKg: e.target.value }))}
              placeholder="Under-ripes + over-ripes + floaters" step="0.1" min="0"
              className={inputCls} onFocus={foc} onBlur={blu} />
            <p className="mt-1 text-xs text-[#A3A3A3]">Will be dried whole as Mbuni — milled later into MH and ML grades</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
              W<sub>Waste</sub> — Physical debris (kg)
            </label>
            <input type="number" value={form.wasteKg} onChange={e => setForm(f => ({ ...f, wasteKg: e.target.value }))}
              placeholder="Twigs, leaves, stones" step="0.1" min="0"
              className={inputCls} onFocus={foc} onBlur={blu} />
          </div>

          {/* Auto-create Mbuni batch toggle */}
          {mbuni > 0 && (
            <div className="flex items-start gap-3 p-4 bg-white border border-[#E8E6E2] rounded-xl">
              <input type="checkbox" id="createMbuni" checked={form.createMbuniBatch}
                onChange={e => setForm(f => ({ ...f, createMbuniBatch: e.target.checked }))}
                className="mt-0.5 w-4 h-4 accent-[#4A7C59]" />
              <div>
                <label htmlFor="createMbuni" className="text-sm font-medium text-[#1A1A1A] cursor-pointer">
                  Auto-create linked Mbuni batch
                </label>
                <p className="text-xs text-[#6B6B6B] mt-0.5">
                  Creates a separate Mbuni batch ({mbuni.toLocaleString()} kg) linked to this primary batch, ready to track on dedicated drying beds
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setStep('reception')} className="flex-1 py-3 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">
              ← Back
            </button>
            <button onClick={() => setStep('confirm')} className="flex-1 py-3 text-white font-medium rounded-lg" style={{ backgroundColor: ACCENT }}>
              Review →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Confirm ───────────────────────────────────────────────── */}
      {step === 'confirm' && (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-white border border-[#E8E6E2] rounded-xl divide-y divide-[#E8E6E2]">
            {[
              { label: 'Primary process', value: form.processType },
              { label: 'Gross intake', value: gross > 0 ? `${gross.toLocaleString()} kg` : '—' },
              { label: 'W_Primary (ripe)', value: primary > 0 ? `${primary.toLocaleString()} kg` : '—' },
              { label: 'W_Mbuni (sorted)', value: mbuni > 0 ? `${mbuni.toLocaleString()} kg` : '—' },
              { label: 'W_Waste', value: waste > 0 ? `${waste.toLocaleString()} kg` : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-[#6B6B6B]">{label}</span>
                <span className="text-sm font-medium text-[#1A1A1A] capitalize">{value}</span>
              </div>
            ))}
            {form.createMbuniBatch && mbuni > 0 && (
              <div className="px-4 py-3 bg-amber-50">
                <p className="text-xs text-amber-700 font-medium">+ Linked Mbuni batch will be created automatically</p>
              </div>
            )}
          </div>

          {!sortingValid && gross > 0 && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-xs">
              Warning: sorting weights don't add up to gross intake. You can still save — adjust later.
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={() => setStep('sorting')} className="flex-1 py-3 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">
              ← Back
            </button>
            <button type="submit" disabled={submitting} className="flex-1 py-3 text-white font-medium rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
              {submitting ? 'Creating...' : 'Create batch'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
